import api from 'axios';

export interface HistoryExtendedMetrics {
  total_venda_valor?: number | null;
  total_compra_valor?: number | null;
  avg_preco_medio_venda?: number | null;
  avg_custo_medio_compra?: number | null;
  total_margem_bruta_estimada?: number | null;
  margem_bruta_estimada_pct?: number | null;
  total_valor_estoque_estimado?: number | null;
  total_saida_perdas?: number | null;
  total_divergencia_estoque?: number | null;
  total_quantidade_pedida?: number | null;
  total_quantidade_entregue?: number | null;
  avg_otif_pontualidade_pct?: number | null;
  avg_otif_completude_pct?: number | null;
  avg_otif_pct?: number | null;
}

export interface HistorySummary extends HistoryExtendedMetrics {
  imports: number;
  products: number;
  cnpjs: number;
  suppliers: number;
  total_saida: number;
  total_entrada: number;
  avg_stock: number;
  rupturas: number;
  avg_coverage: number;
  avg_cmd: number;
  snapshots: number;
  date_from?: string;
  date_to?: string;
  latest_valor_estoque_estimado?: number | null;
  valor_estoque_reference_date?: string;
  venda_valor_count?: number;
  compra_valor_count?: number;
  preco_medio_venda_count?: number;
  custo_medio_compra_count?: number;
  margem_bruta_estimada_count?: number;
  valor_estoque_estimado_count?: number;
  saida_perdas_count?: number;
  divergencia_estoque_count?: number;
  quantidade_pedida_count?: number;
  quantidade_entregue_count?: number;
  otif_pontualidade_pct_count?: number;
  otif_completude_pct_count?: number;
  otif_pct_count?: number;
}

export interface HistorySeriesPoint extends HistoryExtendedMetrics {
  period: string;
  total_saida: number;
  total_entrada: number;
  avg_stock: number;
  rupturas: number;
  avg_coverage: number;
}

export interface HistoryProduct extends HistoryExtendedMetrics {
  ean: string;
  produto: string;
  foto?: string;
  industria?: string;
  cnpjs: number;
  imports: number;
  total_saida: number;
  total_entrada: number;
  avg_stock: number;
  latest_stock: number;
  first_stock: number;
  min_stock: number;
  max_stock: number;
  avg_cmd: number;
  avg_coverage: number;
  stock_variation: number;
  sales_share: number;
  last_reference: string;
}

export interface HistoryImport {
  import_id: number;
  status: 'processing' | 'completed' | 'partial' | 'failed';
  source_files: string[];
  reference_start?: string;
  reference_end?: string;
  total_received: number;
  total_valid: number;
  total_invalid: number;
  supplier_count: number;
  created_at: string;
  completed_at?: string;
}

export interface HistorySupplier extends HistoryExtendedMetrics {
  label: string;
  supplier_id?: number;
  product_count: number;
  total_saida: number;
  avg_stock: number;
  avg_coverage: number;
}

export interface HistorySeasonality extends HistoryExtendedMetrics {
  period: number;
  total_saida: number;
  avg_stock: number;
  products: number;
}

export interface HistoryQualityDetail {
  snapshot_id: number;
  import_id: number;
  ean: string;
  produto: string;
  foto?: string | null;
  industria?: string | null;
  cnpj: string;
  reference_date: string;
  supplier_id?: number | null;
  fornecedor?: string | null;
  saida_perdas: number;
  divergencia_estoque: number;
  estoque_atual: number;
  entrada: number;
  saida: number;
  custo_medio_compra?: number | null;
  perda_valor_estimado?: number | null;
  divergencia_valor_estimado?: number | null;
}

export interface HistoryQualityDetailsMeta {
  total: number;
  loss_total: number;
  divergence_total: number;
  returned: number;
  limit: number;
}

export interface HistoryData {
  summary: HistorySummary;
  series: HistorySeriesPoint[];
  status_distribution: Array<{label: string; value: number}>;
  products: HistoryProduct[];
  suppliers: HistorySupplier[];
  seasonality: HistorySeasonality[];
  quality_details?: HistoryQualityDetail[];
  quality_details_meta?: HistoryQualityDetailsMeta;
  imports: HistoryImport[];
  filters: {
    cnpjs: string[];
    products: Array<{ean: string; produto: string}>;
    suppliers: Array<{supplier_id: number; fornecedor: string}>;
    dates: {from?: string; to?: string};
  };
  granularity: 'day' | 'week' | 'month';
}

export interface HistoryParams {
  date_from?: string;
  date_to?: string;
  cnpj?: string;
  ean?: string;
  supplier_id?: string;
  search?: string;
  granularity?: 'day' | 'week' | 'month';
}

export async function fetchStockHistory(params: HistoryParams): Promise<HistoryData> {
  const response = await api.get<{success: boolean; data: HistoryData; error?: string}>(
    '/sellers/stock-intelligence/history',
    {params}
  );
  if (!response.data.success) throw new Error(response.data.error || 'Não foi possível carregar o histórico.');
  return response.data.data;
}
